// One MQTT connection per tab, shared by everything that needs it.
//
// This module owns the client. Components never touch it: they register message
// handlers and topics here, and those are re-applied to whichever client is current.
// That lets the connection be rebuilt (token refresh, dead socket, laptop waking up,
// tab coming back to the foreground) without any component having to notice.
import mqtt, { MqttClient, IClientOptions } from 'mqtt';
import mqttConfig, { MQTT_PREFIX } from './mqttconfig';
import store from '../../redux/store';

const { v4: uuidv4 } = require('uuid');

const tokenKeyName = 'mqtt_token';
const customAuthorizerName = process.env.REACT_APP_CUSTOM_AUTHORIZER ?? 'DealerPortalCustomAuthorizer';
const isBeta = MQTT_PREFIX.includes('beta');

const KEEPALIVE_S = 60;
const RECONNECT_PERIOD_MS = 5_000;
// Pings go out every KEEPALIVE_S, so a healthy connection always hears from the broker
// more often than this. Longer means the socket is dead even if the browser hasn't noticed.
const BROKER_SILENCE_MS = 2 * KEEPALIVE_S * 1000 + 10_000;
// How long the broker has to answer a ping we send to check the connection.
const PING_TIMEOUT_MS = 10_000;
const CONNECT_TIMEOUT_MS = 30_000;
// Connecting can take several seconds (the custom authorizer runs first), so don't cut
// short a connection that has only been down this long.
const DISCONNECTED_GRACE_MS = 15_000;
const HEALTH_TICK_MS = 5_000;
// A gap this much longer than a tick means the machine slept or the tab was frozen.
const WAKE_GAP_MS = 30_000;
// Keep the connection through a page change instead of dropping and rebuilding it.
const RELEASE_GRACE_MS = 5_000;

export type BrokerStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

// `packet` is passed through so handlers can tell a retained message the broker replayed on
// subscribe from one that was just published.
export type MessageHandler = (topic: string, message: Buffer, packet: { retain?: boolean }) => void;

let client: MqttClient | null = null;
let clientEmail: string | null = null;
let clientToken = '';
let status: BrokerStatus = 'idle';
let users = 0;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let lastPacketAt = 0;
let pingSentAt: number | null = null;
let attemptStartedAt = 0;
let reconnectAttempts = 0;
// When the connection was last lost (or first started), or null while connected. Unlike
// `attemptStartedAt`, retries don't reset it.
let disconnectedSince: number | null = null;

const statusListeners = new Set<() => void>();
const messageHandlers = new Set<MessageHandler>();
// Topic -> number of subscribers. Re-subscribed on every (re)connect.
const topicCounts = new Map<string, number>();

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Another tab may have refreshed the token more recently than this one; the fresher token
// wins so a background tab never reconnects with one that has already expired.
const currentToken = (): string => {
  const { accessToken, expirationTime } = store.getState().user;
  const storedToken = readStorage('accessToken');
  const storedExpiry = Number(readStorage('expirationTime') ?? 0);
  if (storedToken && storedExpiry > (expirationTime ?? 0)) {
    return storedToken;
  }
  return accessToken ?? '';
};

const buildUsername = (token: string) =>
  `?x-amz-customauthorizer-name=${customAuthorizerName}&${tokenKeyName}=${token}&env=${isBeta ? 'beta' : 'prod'}`;

const setStatus = (next: BrokerStatus) => {
  if (status === next) return;
  status = next;
  statusListeners.forEach(listener => listener());
};

// AWS IoT allows at most 8 topic filters per SUBSCRIBE/UNSUBSCRIBE and drops the connection
// if a request has more.
const MAX_TOPICS_PER_REQUEST = 8;

const inBatches = (topics: string[]): string[][] => {
  const batches: string[][] = [];
  for (let i = 0; i < topics.length; i += MAX_TOPICS_PER_REQUEST) {
    batches.push(topics.slice(i, i + MAX_TOPICS_PER_REQUEST));
  }
  return batches;
};

const subscribeOnClient = (c: MqttClient, topics: string[]) => {
  inBatches(topics).forEach(batch => {
    c.subscribe(batch, { qos: 0 }, err => {
      if (err) {
        console.error('[mqtt] Error subscribing to topics:', batch, err);
      } else {
        console.log('[mqtt] Subscribed to', batch.length, 'topics');
      }
    });
  });
};

const startClient = () => {
  const token = currentToken();
  const options: IClientOptions = {
    clientId: `${clientEmail ?? 'web-user'}-${uuidv4()}`,
    port: mqttConfig.port,
    username: buildUsername(token),
    password: '',
    protocol: 'mqtts',
    protocolVersion: 4,
    rejectUnauthorized: true,
    reconnectPeriod: RECONNECT_PERIOD_MS,
    // A refused login (e.g. an expired token) should retry like a dropped socket does.
    reconnectOnConnackError: true,
    keepalive: KEEPALIVE_S,
    // By default every publish postpones the next ping. Status polling publishes often
    // enough that no ping would ever go out, and a dead socket would never be detected.
    reschedulePings: false,
    connectTimeout: CONNECT_TIMEOUT_MS,
    // Subscriptions are re-applied from `topicCounts` on every connect instead.
    resubscribe: false,
  };

  const c = mqtt.connect(`mqtts://${mqttConfig.broker}`, options);
  client = c;
  clientToken = token;
  lastPacketAt = Date.now();
  pingSentAt = null;
  attemptStartedAt = Date.now();
  disconnectedSince = disconnectedSince ?? Date.now();
  setStatus('connecting');

  // Every listener checks `client === c`: a replaced client can still emit while it closes.
  c.on('connect', () => {
    if (client !== c) return;
    console.log('[mqtt] Connected');
    lastPacketAt = Date.now();
    pingSentAt = null;
    disconnectedSince = null;
    reconnectAttempts = 0;
    setStatus('connected');
    subscribeOnClient(c, Array.from(topicCounts.keys()));
  });
  c.on('reconnect', () => {
    if (client !== c) return;
    // mqtt.js reuses the options it was created with, so give it the current token rather
    // than the one this client started with, which may have expired since.
    clientToken = currentToken();
    c.options.username = buildUsername(clientToken);
    attemptStartedAt = Date.now();
    reconnectAttempts += 1;
    const { expirationTime } = store.getState().user;
    const tokenExpired = Boolean(expirationTime && expirationTime < Date.now());
    console.log(`[mqtt] Reconnect attempt ${reconnectAttempts}${tokenExpired ? ' (access token has expired)' : ''}`);
    setStatus('reconnecting');
  });
  c.on('close', () => {
    if (client !== c) return;
    if (status === 'connected') console.log('[mqtt] Connection closed, reconnecting');
    disconnectedSince = disconnectedSince ?? Date.now();
    setStatus('reconnecting');
  });
  c.on('packetreceive', () => {
    if (client !== c) return;
    lastPacketAt = Date.now();
    pingSentAt = null;
  });
  c.on('message', (topic, message, packet) => {
    if (client !== c) return;
    messageHandlers.forEach(handler => {
      try {
        handler(topic, message, packet);
      } catch (err) {
        console.error('[mqtt] Message handler failed for topic:', topic, err);
      }
    });
  });
  // Always keep an error listener: an unhandled 'error' event would throw.
  c.on('error', err => {
    if (client !== c) return;
    console.error('[mqtt] client error:', err?.message ?? err);
  });
};

const stopClient = () => {
  const c = client;
  client = null;
  if (c) {
    c.end(true);
  }
};

const restart = (reason: string) => {
  console.log(`[mqtt] Rebuilding connection: ${reason}`);
  stopClient();
  startClient();
};

// Check the connection now instead of waiting for the keepalive to notice a problem.
const verifyConnection = (reason: string) => {
  if (!client) return;
  if (!client.connected) {
    // Don't wait out a (possibly throttled) retry loop, but let a connection that has only
    // just dropped or started finish connecting.
    const downFor = disconnectedSince === null ? 0 : Date.now() - disconnectedSince;
    if (downFor > DISCONNECTED_GRACE_MS) {
      restart(`${reason}, disconnected for ${Math.round(downFor / 1000)}s`);
    }
    return;
  }
  if (pingSentAt === null) {
    pingSentAt = Date.now();
    client.sendPing();
  }
};

let lastTickAt = 0;
const healthCheck = () => {
  const now = Date.now();
  const slept = now - lastTickAt > HEALTH_TICK_MS + WAKE_GAP_MS;
  lastTickAt = now;
  if (!client) return;

  if (slept) {
    verifyConnection('woke up');
    return;
  }
  if (client.connected) {
    if (pingSentAt !== null && now - pingSentAt > PING_TIMEOUT_MS) {
      restart('broker did not answer a ping');
    } else if (now - lastPacketAt > BROKER_SILENCE_MS) {
      restart('nothing heard from the broker');
    }
    return;
  }
  // Safety net for an attempt that never resolves. mqtt.js's own connect timeout should
  // close it first and schedule a retry, which resets `attemptStartedAt`.
  if (now - attemptStartedAt > CONNECT_TIMEOUT_MS + DISCONNECTED_GRACE_MS) {
    restart('connect attempt never finished');
  }
};

const handleVisibility = () => {
  if (document.visibilityState === 'visible') verifyConnection('tab visible');
};
const handleOnline = () => verifyConnection('network back online');
const handlePageShow = (event: PageTransitionEvent) => {
  if (event.persisted) verifyConnection('restored from back/forward cache');
};

let lastSeenReduxToken: string | null = null;
const handleStoreChange = () => {
  const token = store.getState().user.accessToken;
  if (!token || token === lastSeenReduxToken) return;
  lastSeenReduxToken = token;
  // The broker authorizes the connection with the token it was opened with, so move to the
  // new one before the old one expires. Skip if this client already picked it up.
  if (client && token !== clientToken) {
    restart('access token changed');
  }
};

let healthTimer: ReturnType<typeof setInterval> | null = null;
let unsubscribeStore: (() => void) | null = null;

const startWatchers = () => {
  if (healthTimer) return;
  lastTickAt = Date.now();
  healthTimer = setInterval(healthCheck, HEALTH_TICK_MS);
  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('online', handleOnline);
  window.addEventListener('pageshow', handlePageShow);
  lastSeenReduxToken = store.getState().user.accessToken;
  unsubscribeStore = store.subscribe(handleStoreChange);
};

const stopWatchers = () => {
  if (healthTimer) clearInterval(healthTimer);
  healthTimer = null;
  document.removeEventListener('visibilitychange', handleVisibility);
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('pageshow', handlePageShow);
  unsubscribeStore?.();
  unsubscribeStore = null;
};

/**
 * Keep the shared connection open for as long as the returned release function hasn't been
 * called. The connection closes a few seconds after the last user releases it.
 */
export const acquireConnection = (email: string): (() => void) => {
  users += 1;
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  if (!client || clientEmail !== email) {
    clientEmail = email;
    restart(client ? 'user changed' : 'first user');
  }
  startWatchers();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    users -= 1;
    if (users > 0) return;
    releaseTimer = setTimeout(() => {
      releaseTimer = null;
      if (users > 0) return;
      stopWatchers();
      stopClient();
      disconnectedSince = null;
      setStatus('idle');
    }, RELEASE_GRACE_MS);
  };
};

/** Subscribe to topics until the returned function is called. Survives reconnects. */
export const subscribeTopics = (topics: string[]): (() => void) => {
  const added = topics.filter(topic => {
    const count = topicCounts.get(topic) ?? 0;
    topicCounts.set(topic, count + 1);
    return count === 0;
  });
  if (client?.connected) subscribeOnClient(client, added);

  let done = false;
  return () => {
    if (done) return;
    done = true;
    const removed = topics.filter(topic => {
      const count = (topicCounts.get(topic) ?? 1) - 1;
      if (count > 0) {
        topicCounts.set(topic, count);
        return false;
      }
      topicCounts.delete(topic);
      return true;
    });
    const c = client;
    if (c?.connected) {
      inBatches(removed).forEach(batch => {
        c.unsubscribe(batch, err => {
          if (err) console.warn('[mqtt] Error unsubscribing from topics:', batch, err);
        });
      });
    }
  };
};

/** Receive every message the connection gets until the returned function is called. */
export const addMessageHandler = (handler: MessageHandler): (() => void) => {
  messageHandlers.add(handler);
  return () => {
    messageHandlers.delete(handler);
  };
};

export const getBrokerStatus = (): BrokerStatus => status;

export const subscribeBrokerStatus = (listener: () => void): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

export const publishMessage = (topic: string, message: string, retain: boolean = true) => {
  // mqtt.js would queue the message and send it whenever the connection comes back, which
  // for a spa command could be long after the user gave up on it.
  if (!client?.connected) {
    console.warn(`[mqtt] Not connected (${status}), message not sent to topic:`, topic, message);
    return;
  }

  client.publish(topic, message, { qos: 1, retain }, (err) => {
    if (err) {
      console.error('Error publishing message to topic:', topic, message, err);
    } else {
      console.log('Successfully published message to topic:', topic, message);
    }
  });
};
